import React from "react";
import SEO from "../components/SEO";
import { MEDIA, CATEGORIES, USE_CASES } from "../lib/constants";

export default function Education() {
  return (
    <>
      <SEO
        title="Cannabis Product Education | District Wellness Center"
        description="Learn about cannabis flower, edibles, vapes, concentrates, and tinctures. Dosing, effects, and choosing the right product for your needs."
        path="/education"
        breadcrumbs={[{ name: "Education", path: "/education" }]}
      />
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">Product Education</div>
            <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight">
              Understand what<br/>you're <em className="text-brand">consuming</em>.
            </h1>
          </div>
          <div className="lg:col-span-4 text-ink-muted text-lg leading-relaxed">
            Cannabis works best when you know what you're working with. Here's the plain-English version.
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-5">
          {CATEGORIES.map((c, i) => (
            <article key={c.name} className="bg-white border border-black/10 p-10 hover:border-brand transition-colors" data-testid={`edu-${c.name.toLowerCase()}`}>
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-3">Category 0{i+1}</div>
              <h3 className="font-serif text-4xl">{c.name}</h3>
              <p className="mt-4 text-ink-muted">{c.copy}</p>
              <p className="mt-4 text-sm text-ink-muted leading-relaxed">
                {c.name === "Flower" && "Cured cannabis flower ready to be ground and smoked, vaporized, or rolled. Look at THC, terpene profile, and cultivator reputation — not just potency."}
                {c.name === "Edibles" && "Infused food and drink. Effects onset 30–90 min and last 4–6 hrs. Start with 2.5–5 mg THC if new — more is not better."}
                {c.name === "Vapes" && "510 cartridges and all-in-one disposables. Clean extraction, no additives. Fast onset, discreet, portable."}
                {c.name === "Concentrates" && "Live rosin, live resin, badder, diamonds. Highly potent — for experienced users. Great flavor when done right."}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mb-12">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-3">By Need</div>
            <h2 className="font-serif text-5xl leading-[0.95]">Match product to purpose.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {USE_CASES.map((u) => (
              <article key={u.title} className="p-8 bg-[#FAFAFA] border border-black/10">
                <h3 className="font-serif text-2xl">{u.title}</h3>
                <p className="mt-3 text-sm text-ink-muted leading-relaxed">{u.copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" data-testid="edu-infographics">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-3">Visual Guides</div>
              <h2 className="font-serif text-5xl leading-[0.95]">Learn at a glance.</h2>
              <p className="mt-5 text-ink-muted text-base leading-relaxed">
                Save, screenshot or print these DWC quick-reference guides. Perfect for
                first-time patients trying to understand terpenes, cannabinoids, strain
                categories and edible dosing.
              </p>
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-ink/40">
              Educate · Empower · Elevate
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { src: MEDIA.infoStrainTypes,  title: "Indica · Sativa · Hybrid", desc: "Choose your experience by strain type.", tid: "infographic-strains" },
              { src: MEDIA.infoTerpenes,     title: "Terpenes",                  desc: "Aromatic compounds & the Entourage Effect.", tid: "infographic-terpenes" },
              { src: MEDIA.infoCannabinoids, title: "Cannabinoids",              desc: "THC, CBD, CBG, CBN and how they work.", tid: "infographic-cannabinoids" },
              { src: MEDIA.infoEdibleSafety, title: "Edible Safety",             desc: "Know your dose. Start low, go slow.", tid: "infographic-edibles" },
            ].map((g) => (
              <a
                key={g.tid}
                href={g.src}
                target="_blank"
                rel="noreferrer"
                className="group flex flex-col bg-[#FAFAFA] border border-black/10 hover:border-brand transition-all"
                data-testid={g.tid}
              >
                <div className="aspect-[3/4] overflow-hidden bg-white border-b border-black/5">
                  <img
                    src={g.src}
                    alt={`${g.title} — District Wellness Center educational guide`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="font-serif text-lg text-ink group-hover:text-brand transition-colors leading-tight">
                    {g.title}
                  </div>
                  <div className="text-xs text-ink/55 mt-1.5 leading-snug">{g.desc}</div>
                  <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-brand group-hover:underline">
                    View full guide →
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-ink text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <img src={MEDIA.macroFlower} alt="Macro flower" className="w-40 h-40 object-cover mx-auto mb-10 border border-white/10" />
          <h2 className="font-serif text-5xl sm:text-6xl leading-[0.95]">
            Still unsure? <em className="text-brand">Ask us</em>.
          </h2>
          <p className="mt-6 text-white/70 max-w-xl mx-auto">
            Our patient specialists are trained to guide you to the right product — never the most expensive one.
          </p>
        </div>
      </section>
    </>
  );
}
